import {
  CookieConsentChoices,
  CookieConsentStoredData,
  createCookieConsent,
  detectGeo,
  GeoLookup,
  getCookieConsent,
  REJECT_OPTIONAL_COOKIE_DEFAULTS,
  getDefaultConsentChoices,
  pushCookieConsentDataLayerEvent,
  writeCookieConsent,
} from '@/common/cookie-consent';
import {useEffect, useState} from 'react';
import {FaCookieBite} from 'react-icons/fa';
import styles from './CookieConsentBanner.module.scss';

type OptionalCookieConsentCategory = Exclude<keyof CookieConsentChoices, 'essential'>;

type CookieConsentState = {
  storedConsent: CookieConsentStoredData | null;
  choices: CookieConsentChoices;
  isPanelOpen: boolean;
  hasEditedChoices?: boolean;
};

type GetDefaultChoicesParams = {
  geo: GeoLookup | null;
  globalPrivacyControlEnabled: boolean;
};

type HandleToggleChoiceParams = {
  category: OptionalCookieConsentCategory;
  value: boolean;
};

type PreferenceRowProps = {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  statusText?: string;
  onToggle?: (value: boolean) => void;
};

const COOKIE_POLICY_URL = 'https://www.tocafootball.com/policies/cookie-policy';

const isCookieBannerEnabled = () => import.meta.env.VITE_SHOW_COOKIE_BANNER === 'true';

const isGlobalPrivacyControlEnabled = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return navigator.globalPrivacyControl === true;
};

const getDefaultChoices = ({geo, globalPrivacyControlEnabled}: GetDefaultChoicesParams): CookieConsentChoices => {
  const defaultChoices = getDefaultConsentChoices(geo);

  return {
    ...defaultChoices,
    marketing: globalPrivacyControlEnabled ? false : defaultChoices.marketing,
  };
};

const getInitialCookieConsentState = (): CookieConsentState => {
  const storedConsent = getCookieConsent();

  return {
    storedConsent,
    choices: storedConsent ? storedConsent.choices : REJECT_OPTIONAL_COOKIE_DEFAULTS,
    isPanelOpen: false,
  };
};

const PreferenceRow = ({
  id,
  title,
  description,
  checked,
  disabled = false,
  statusText,
  onToggle,
}: PreferenceRowProps) => {
  const handleClick = () => {
    if (disabled || !onToggle) {
      return;
    }

    onToggle(!checked);
  };

  return (
    <div className={styles.preferenceRow}>
      <div className={styles.preferenceText}>
        <label htmlFor={id}>{title}</label>
        <p>{description}</p>
        {statusText ? <span>{statusText}</span> : null}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-label={`${title} consent`}
        aria-checked={checked}
        disabled={disabled}
        className={`${styles.toggle} ${checked ? styles.toggleOn : ''}`}
        onClick={handleClick}
      >
        <span />
      </button>
    </div>
  );
};

const CookieConsentBannerContent = () => {
  const globalPrivacyControlEnabled = isGlobalPrivacyControlEnabled();
  const [consentState, setConsentState] = useState<CookieConsentState>(getInitialCookieConsentState);
  const [showGpcNotice, setShowGpcNotice] = useState(false);

  useEffect(() => {
    const initializeConsentState = async () => {
      if (globalPrivacyControlEnabled) {
        setShowGpcNotice(true);
      }

      // Runs on every load so the detected region is always published, even when consent is already stored.
      const geo = await detectGeo();
      setConsentState((currentState) => {
        // Don't let a late lookup overwrite consent that's saved or choices the user already changed.
        if (currentState.storedConsent || currentState.hasEditedChoices) {
          return currentState;
        }

        return {...currentState, choices: getDefaultChoices({geo, globalPrivacyControlEnabled})};
      });
    };

    initializeConsentState();
  }, [globalPrivacyControlEnabled]);

  const handleToggleChoice = ({category, value}: HandleToggleChoiceParams) => {
    setConsentState((currentState) => ({
      ...currentState,
      hasEditedChoices: true,
      choices: {
        ...currentState.choices,
        [category]: value,
      },
    }));
  };

  const handleOpenPreferences = () => {
    setConsentState((currentState) => ({
      ...currentState,
      isPanelOpen: true,
    }));
  };

  const handleMinimizePreferences = () => {
    setConsentState((currentState) => ({
      ...currentState,
      isPanelOpen: false,
    }));
  };

  const handleSavePreferences = () => {
    const consent = createCookieConsent({
      choices: {
        ...consentState.choices,
        essential: true,
      },
    });

    writeCookieConsent({consent});
    pushCookieConsentDataLayerEvent(consent);
    setConsentState({
      storedConsent: consent,
      choices: consent.choices,
      isPanelOpen: false,
    });
  };

  const handleRejectOptionalPreferences = () => {
    const consent = createCookieConsent({
      choices: REJECT_OPTIONAL_COOKIE_DEFAULTS,
    });

    writeCookieConsent({consent});
    pushCookieConsentDataLayerEvent(consent);
    setConsentState({
      storedConsent: consent,
      choices: consent.choices,
      isPanelOpen: false,
    });
  };

  const handleAcceptAllPreferences = () => {
    const consent = createCookieConsent({
      choices: {
        essential: true,
        analytics: true,
        marketing: true,
        personalization: true,
      },
    });

    writeCookieConsent({consent});
    pushCookieConsentDataLayerEvent(consent);
    setConsentState({
      storedConsent: consent,
      choices: consent.choices,
      isPanelOpen: false,
    });
  };

  const handleDismissGpcNotice = () => {
    setShowGpcNotice(false);
  };

  const isCompactPromptOpen = !consentState.isPanelOpen && !consentState.storedConsent;
  const gpcNoticeClassName = `${styles.gpcNotice} ${consentState.isPanelOpen ? styles.gpcNoticeWithPanel : ''} ${
    isCompactPromptOpen ? styles.gpcNoticeWithCompactPrompt : ''
  }`;

  return (
    <>
      {consentState.isPanelOpen ? (
        <section className={styles.panel} aria-label="Cookie preferences">
          <div className={styles.panelContent}>
            <div className={styles.header}>
              <div>
                <p className={styles.eyebrow}>Cookie preferences</p>
              </div>
              <div className={styles.headerActions}>
                {consentState.storedConsent ? (
                  <button type="button" className={styles.closeButton} onClick={handleSavePreferences}>
                    Save and close
                  </button>
                ) : null}
                <button
                  type="button"
                  className={styles.minimizeButton}
                  onClick={handleMinimizePreferences}
                  aria-label="Minimize cookie preferences"
                >
                  &minus;
                </button>
              </div>
            </div>

            <p className={styles.description}>
              We use cookies to support essential site features and to improve analytics, marketing, and
              personalization. Learn more in our{' '}
              <a href={COOKIE_POLICY_URL} target="_blank" rel="noreferrer">
                cookie policy
              </a>
              .
            </p>

            <div className={styles.preferenceList}>
              <PreferenceRow
                id="cookie-consent-essential"
                title="Essential"
                description="Required for core site functionality and security."
                checked={true}
                disabled={true}
              />
              <PreferenceRow
                id="cookie-consent-analytics"
                title="Analytics"
                description="Helps us understand site usage and improve the portal."
                checked={consentState.choices.analytics}
                onToggle={(value) => handleToggleChoice({category: 'analytics', value})}
              />
              <PreferenceRow
                id="cookie-consent-marketing"
                title="Marketing"
                description="Supports advertising, campaign measurement, and related marketing features."
                checked={consentState.choices.marketing}
                statusText={globalPrivacyControlEnabled ? 'Off by default due to Global Privacy Control' : undefined}
                onToggle={(value) => handleToggleChoice({category: 'marketing', value})}
              />
              <PreferenceRow
                id="cookie-consent-personalization"
                title="Personalization"
                description="Allows us to tailor content and experiences based on your activity."
                checked={consentState.choices.personalization}
                onToggle={(value) => handleToggleChoice({category: 'personalization', value})}
              />
            </div>

            <div className={styles.actions}>
              <button type="button" className={styles.primaryButton} onClick={handleSavePreferences}>
                Save Preferences
              </button>
            </div>
          </div>
        </section>
      ) : !consentState.storedConsent ? (
        <section className={styles.compactPrompt} aria-labelledby="cookie-consent-prompt-heading">
          <div className={styles.compactPromptText}>
            <h2 id="cookie-consent-prompt-heading">
              We use cookies to improve MyTOCA.{' '}
              <a href={COOKIE_POLICY_URL} target="_blank" rel="noreferrer">
                Cookie policy
              </a>
            </h2>
          </div>
          <div className={styles.compactPromptActions}>
            <button type="button" className={styles.secondaryButton} onClick={handleOpenPreferences}>
              Manage
            </button>
            <button type="button" className={styles.secondaryButton} onClick={handleRejectOptionalPreferences}>
              Reject Optional
            </button>
            <button type="button" className={styles.primaryButton} onClick={handleAcceptAllPreferences}>
              Accept All
            </button>
          </div>
        </section>
      ) : (
        <button
          type="button"
          className={styles.floatingButton}
          onClick={handleOpenPreferences}
          aria-label="Cookie settings"
        >
          <FaCookieBite aria-hidden="true" />
        </button>
      )}

      {showGpcNotice ? (
        <aside className={gpcNoticeClassName}>
          <div>
            <strong>Privacy signal detected</strong>
            <p>Your browser sent a Global Privacy Control signal, so marketing cookies are off by default. You can turn them on in cookie preferences.</p>
          </div>
          <button type="button" onClick={handleDismissGpcNotice} aria-label="Dismiss privacy signal notice">
            Close
          </button>
        </aside>
      ) : null}
    </>
  );
};

export const CookieConsentBanner = () => {
  if (!isCookieBannerEnabled()) {
    return null;
  }

  return <CookieConsentBannerContent />;
};
